import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  ButtonComponent,
  EmptyComponent,
  InputComponent,
  SkeletonComponent,
  TableComponent,
  type TableConfig,
} from 'nebula-ui-kit';
import { finalize } from 'rxjs';
import { AdminTenantsService } from '../../../../data-access/src/lib/admin-users/admin-tenants.service';
import { DashboardService } from '../../../../data-access/src/lib/dashboard/dashboard.service';
import type { AdminTenantSummary } from '../../../../domain/src/lib/admin-users/admin-user.model';
import { PageHeaderComponent, toModuleAccessErrorMessage, VaultPageAlertComponent } from '@nebula-shared/ui';
import { vaultUserIsSuperadminFromSession } from '@nebula/shared-vault/data-access';
import { ServerContextService } from '../servers/server-context.service';
import {
  bootstrapVaultAdminModuleAccess,
  createAdminModuleAccessSignal,
} from '../admin-users/vault-admin-module-access.util';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  statusLabel: string;
}

@Component({
  selector: 'app-admin-servers',
  templateUrl: './admin-servers.component.html',
  styleUrls: ['./admin-servers.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    PageHeaderComponent,
    ButtonComponent,
    InputComponent,
    TableComponent,
    EmptyComponent,
    SkeletonComponent,
    VaultPageAlertComponent,
  ],
})
export class AdminServersComponent implements OnInit {
  private readonly tenantsApi = inject(AdminTenantsService);
  private readonly dashboard = inject(DashboardService);
  private readonly serverContext = inject(ServerContextService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly canManage = createAdminModuleAccessSignal('superadmin_only');

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly tenants = signal<AdminTenantSummary[]>([]);
  readonly tenantId = signal('');
  readonly info = signal<string | null>(null);

  readonly tableConfig = signal<TableConfig<TenantRow>>({
    columns: [
      { key: 'name', label: 'Empresa', sortable: true },
      { key: 'slug', label: 'Slug', sortable: true },
      { key: 'statusLabel', label: 'Estado', sortable: true },
    ],
    ariaLabel: 'Empresas y servidores',
  });

  readonly tableData = signal<TenantRow[]>([]);

  ngOnInit(): void {
    this.tenantId.set(this.serverContext.getContext().tenantId ?? '');
    bootstrapVaultAdminModuleAccess(
      this.canManage,
      'superadmin_only',
      this.dashboard,
      this.cdr,
      () => this.reload(),
    );
    if (!vaultUserIsSuperadminFromSession()) {
      this.loading.set(false);
    }
  }

  reload(): void {
    if (!this.canManage()) return;
    this.loading.set(true);
    this.error.set(null);
    this.tenantsApi
      .list()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (list) => {
          this.tenants.set(list);
          this.tableData.set(
            list.map((t) => ({
              id: t.id,
              name: t.name,
              slug: t.slug,
              statusLabel: t.is_active !== false ? 'Activa' : 'Inactiva',
            })),
          );
        },
        error: (err: unknown) =>
          this.error.set(
            toModuleAccessErrorMessage(err, {
              moduleLabel: 'Configuración de servidores',
              fallback: 'No se pudo cargar el listado de empresas.',
            }),
          ),
      });
  }

  saveContext(): void {
    const normalized = this.tenantId().trim();
    if (!normalized) {
      this.error.set('Debes ingresar un Tenant UUID.');
      this.info.set(null);
      return;
    }
    this.serverContext.setContext({ tenantId: normalized });
    this.tenantId.set(normalized);
    this.error.set(null);
    this.info.set('Tenant UUID global guardado para las peticiones de Vault.');
  }

  clearContext(): void {
    this.serverContext.clearContext();
    this.tenantId.set('');
    this.error.set(null);
    this.info.set('Tenant UUID limpiado.');
  }
}
